import { gql } from '@apollo/client';
import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { ImageIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { formatCalendarDate, formatDaysUntil, formatMoneyShort, toCurrency } from '../../../utils/formatting';
import { formatRecurringType, getAverageAmountLabel, getFrequencyLabel } from '../../../utils/recurring';
import BillSlideout from '../../slideouts/BillSlideout';
import RecurringTransactionSlideout from '../../slideouts/RecurringTransactionSlideout';
import TransactionSlideout from '../../slideouts/TransactionSlideout';
import { MerchantPrimaryElement } from '../elements';
import { ErrorTableState, LoadingTableState } from '../helpers';

import type { ObligationsTableRowFragment, ObligationMerchantFragment } from './__generated__/BaseObligationsTable.gql';
import type { CalendarDate } from '@internationalized/date';

export interface IObligationsTableRowData {
  id: string;
  entityType: ObligationsTableRowFragment['__typename'];
  name: string;
  merchant?: ObligationMerchantFragment;
  recurringTransactionId?: string;
  type?: string;
  date: CalendarDate | null;
  frequency: string;
  document: boolean;
  amount: string;
}

export enum ObligationsDateFormat {
  Absolute = 'absolute',
  Relative = 'relative',
}

export enum ObligationsAmountFormat {
  Short = 'short',
  Full = 'full',
}

export interface IDateStatus {
  label: string;
  className?: string;
}

interface IBaseObligationsTableProps {
  title: string;
  subtitle?: React.ReactNode;
  rows: ObligationsTableRowFragment[];
  dateFormat: ObligationsDateFormat;
  amountFormat?: ObligationsAmountFormat;
  getDateStatus?: (row: IObligationsTableRowData) => IDateStatus | null;
  emptyStateMessage?: string;
  showChargeSummary?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
}

const ObligationMerchantFragmentDocument = gql`
  fragment ObligationMerchant on Merchant {
    id
    name
    logoUrl
  }
`;

const ObligationsTableRowFragmentDocument = gql`
  fragment ObligationsTableRow on Obligation {
    __typename
    ... on Bill {
      id
      providerName
      dueDate
      amountDue
      merchant {
        ...ObligationMerchant
      }
      recurringTransaction {
        id
        frequency
        merchant {
          ...ObligationMerchant
        }
      }
      documents {
        id
      }
    }
    ... on RecurringTransaction {
      id
      description
      type
      nextOccurrence
      frequency
      amountRange
      merchant {
        ...ObligationMerchant
      }
    }
    ... on Transaction {
      id
      description
      date
      amount
      merchant {
        ...ObligationMerchant
      }
      recurringTransaction {
        id
        type
        frequency
        merchant {
          ...ObligationMerchant
        }
      }
      bill {
        id
        documents {
          id
        }
      }
    }
  }
  ${ObligationMerchantFragmentDocument}
`;

export const getObligationTableRowData = (
  obligation: ObligationsTableRowFragment,
  amountFormat: ObligationsAmountFormat = ObligationsAmountFormat.Short
): IObligationsTableRowData => {
  const useFull = amountFormat === ObligationsAmountFormat.Full;
  const formatAmount = (value: number | string) =>
    useFull ? toCurrency(value) : formatMoneyShort(value, { showCents: true });

  switch (obligation.__typename) {
    case 'Bill': {
      const billRecurringFrequency = obligation.recurringTransaction?.frequency;
      return {
        id: obligation.id,
        entityType: obligation.__typename,
        name: obligation.providerName,
        merchant: obligation.merchant ?? undefined,
        recurringTransactionId: obligation.recurringTransaction?.id ?? undefined,
        type: 'Bill',
        date: obligation.dueDate,
        frequency: billRecurringFrequency ? getFrequencyLabel(billRecurringFrequency) : 'One-time',
        document: obligation.documents.length > 0,
        amount: formatAmount(obligation.amountDue),
      };
    }
    case 'RecurringTransaction':
      return {
        id: obligation.id,
        entityType: obligation.__typename,
        name: obligation.description,
        merchant: obligation.merchant ?? undefined,
        type: formatRecurringType(obligation.type),
        date: obligation.nextOccurrence ?? null,
        frequency: getFrequencyLabel(obligation.frequency),
        document: false,
        amount: getAverageAmountLabel(obligation.amountRange),
      };
    case 'Transaction': {
      const recurringType = obligation.recurringTransaction?.type;
      const recurringFrequency = obligation.recurringTransaction?.frequency;
      return {
        id: obligation.id,
        entityType: obligation.__typename,
        name: obligation.description,
        merchant: obligation.merchant ?? undefined,
        type: recurringType ? formatRecurringType(recurringType) : undefined,
        date: obligation.date,
        frequency: recurringFrequency ? getFrequencyLabel(recurringFrequency) : 'Fluctuating',
        document: !!obligation.bill && obligation.bill.documents.length > 0,
        amount: formatAmount(obligation.amount),
      };
    }
    default:
      throw new Error('Unsupported obligation type');
  }
};

const formatObligationDate = (date: CalendarDate | null, dateFormat: ObligationsDateFormat): string => {
  if (!date) {
    return 'Unknown';
  }

  if (dateFormat === ObligationsDateFormat.Absolute) {
    return formatCalendarDate(date, 'medium');
  }

  return formatDaysUntil(date);
};

const getAbsoluteDateLabel = (date: CalendarDate | null): string => {
  if (!date) {
    return 'Unknown';
  }
  return formatCalendarDate(date, 'medium');
};

const getAverageAmount = (amountRange: string[]): number => {
  if (amountRange.length === 0) {
    return 0;
  }

  return amountRange.reduce((total, amount) => total + Number(amount), 0) / amountRange.length;
};

const getObligationChargeAmount = (obligation: ObligationsTableRowFragment): number => {
  switch (obligation.__typename) {
    case 'Bill':
      return Math.abs(Number(obligation.amountDue));
    case 'RecurringTransaction':
      return Math.abs(getAverageAmount(obligation.amountRange));
    case 'Transaction':
      return Math.abs(Number(obligation.amount));
    default:
      return 0;
  }
};

const canOpenSlideoutForRow = (row: IObligationsTableRowData): boolean => {
  switch (row.entityType) {
    case 'Transaction':
    case 'Bill':
    case 'RecurringTransaction':
      return true;
    default:
      return false;
  }
};

const BaseObligationsTable = ({
  title,
  subtitle,
  rows,
  dateFormat,
  amountFormat = ObligationsAmountFormat.Short,
  getDateStatus,
  emptyStateMessage = 'No obligations found',
  showChargeSummary = false,
  isLoading = false,
  isLoadingMore = false,
  hasError = false,
  hasNextPage = false,
  onLoadMore,
}: IBaseObligationsTableProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [selectedBillId, setSelectedBillId] = React.useState<string | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
  const [selectedRecurringTransactionId, setSelectedRecurringTransactionId] = React.useState<string | null>(null);
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: onLoadMore ?? (() => undefined),
    hasMore: hasNextPage,
    isLoading: isLoadingMore,
    enabled: !isCollapsed && !isLoading && Boolean(onLoadMore),
    threshold: 1,
  });

  const obligationRowData = rows.map((row) => getObligationTableRowData(row, amountFormat));
  const totalChargesAmount = rows.reduce((total, obligation) => {
    return total + getObligationChargeAmount(obligation);
  }, 0);

  const handleRowClick = React.useCallback((row: IObligationsTableRowData) => {
    switch (row.entityType) {
      case 'Transaction':
        setSelectedTransactionId(row.id);
        return;
      case 'Bill':
        setSelectedBillId(row.id);
        return;
      case 'RecurringTransaction':
        setSelectedRecurringTransactionId(row.id);
        return;
      default:
        return;
    }
  }, []);

  return (
    <TableCard.Root className="min-w-180 shrink-0 border-none">
      <TableCard.Header
        title={title}
        subtitle={subtitle}
        contentTrailing={
          <div className="flex items-center gap-3">
            {showChargeSummary && !isLoading && !hasError && (
              <span className="text-tertiary text-xs font-medium">
                {obligationRowData.length} charges for {toCurrency(totalChargesAmount)}
              </span>
            )}
            <CollapseButton isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
          </div>
        }
      />
      {!isCollapsed && (
        <>
          {isLoading && <LoadingTableState />}
          {!isLoading && hasError && <ErrorTableState />}
          {!isLoading && !hasError && obligationRowData.length === 0 && (
            <div className="flex h-56 items-center justify-center px-5 text-center">
              <div className="text-primary text-lg font-medium">{emptyStateMessage}</div>
            </div>
          )}
          {!isLoading && !hasError && obligationRowData.length > 0 && (
            <Table aria-label={`${title} obligations table`} className="w-full">
              <Table.Header>
                <Table.Head label="Date" className="px-4" isRowHeader />
                <Table.Head label="Name/type" className="px-4" />
                <Table.Head label="Frequency" className="px-4" />
                <Table.Head label="Bill" className="px-4" />
                <Table.Head label="Amount" className="flex justify-end px-4" />
              </Table.Header>
              <Table.Body items={obligationRowData}>
                {(row) => {
                  const dateStatus = getDateStatus?.(row) ?? null;

                  return (
                    // Rows are clickable only when a slideout can be opened for that row type.
                    <Table.Row
                      key={row.id}
                      className={canOpenSlideoutForRow(row) ? 'h-16 cursor-pointer' : 'h-16'}
                      onClick={canOpenSlideoutForRow(row) ? () => handleRowClick(row) : undefined}
                    >
                      <Table.Cell className="w-32 px-4 py-3">
                        <div>
                          {dateFormat === ObligationsDateFormat.Relative && row.date != null ? (
                            <Tooltip title={getAbsoluteDateLabel(row.date)}>
                              <TooltipTrigger>
                                <div className="text-primary whitespace-nowrap text-base font-medium">
                                  {formatObligationDate(row.date, dateFormat)}
                                </div>
                              </TooltipTrigger>
                            </Tooltip>
                          ) : (
                            <div className="text-primary whitespace-nowrap text-base font-medium">
                              {formatObligationDate(row.date, dateFormat)}
                            </div>
                          )}
                          {dateStatus ? (
                            <div
                              className={`text-tertiary whitespace-nowrap text-sm font-medium ${dateStatus.className}`}
                            >
                              {dateStatus.label}
                            </div>
                          ) : null}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="w-full max-w-0 px-4 py-3">
                        <div className="min-w-0">
                          <MerchantPrimaryElement
                            merchant={row.merchant}
                            fallbackDescription={row.name}
                            secondaryText={row.type}
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell className="w-28 px-4 py-3">
                        <div className="text-tertiary text-sm">{row.frequency}</div>
                      </Table.Cell>
                      <Table.Cell className="w-16 px-4 py-3">
                        {row.document ? <ImageIcon className="text-quaternary size-5" /> : null}
                      </Table.Cell>
                      <Table.Cell className="w-0 min-w-24 px-4 py-3">
                        <div className="text-primary text-right text-base font-medium">{row.amount}</div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          )}
          {!isLoading && !hasError && hasNextPage && onLoadMore && (
            <div className="bg-secondary_subtle border-secondary flex items-center justify-center gap-3 border-t px-6 py-4">
              <div ref={sentinelRef} className="flex items-center justify-center">
                <LoadingIndicator size="sm" />
              </div>
            </div>
          )}
        </>
      )}
      <BillSlideout billId={selectedBillId} isOpen={!!selectedBillId} onClose={() => setSelectedBillId(null)} />
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={!!selectedTransactionId}
        onClose={() => setSelectedTransactionId(null)}
      />
      <RecurringTransactionSlideout
        recurringTransactionId={selectedRecurringTransactionId}
        isOpen={!!selectedRecurringTransactionId}
        onClose={() => setSelectedRecurringTransactionId(null)}
      />
    </TableCard.Root>
  );
};

BaseObligationsTable.Fragment = ObligationsTableRowFragmentDocument;

export default BaseObligationsTable;
